import { useState, useEffect } from "react";
import { auth, db } from "../firebase/config";
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  sendEmailVerification
} from "firebase/auth";
import { ref, set } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { generateClinicalId } from "../utils/idGenerator";
import { ShieldCheck, Lock, Mail, User, ArrowRight, Activity } from "lucide-react";
import "./admin-auth.css";

const AdminAuthPage = () => {
  const { currentUser, userData, loading: authLoading } = useAuth();
  const { showAlert } = useToast();
  const [isLogin, setIsLogin] = useState(true);
  const navigate = useNavigate();


  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin", // Hardcoded
  });

  const [loading, setLoading] = useState(false);


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, formData.email, formData.password);
      } else {
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          formData.email, 
          formData.password
        );
        const user = userCredential.user;

        // Provision Unique Administrative Identity
        const customId = await generateClinicalId("admin");

        // Mandatory administrative email verification
        await sendEmailVerification(user);

        // Seed administrator with system_root status and hardened schema
        const adminProfile = {
          id: user.uid, // Legacy compatibility
          uid: user.uid,
          customId: customId,
          name: formData.name,
          email: formData.email,
          role: "admin",
          permissions: "FULL_ACCESS",
          status: "system_root",
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          accessLevel: "FULL_CONTROL",
          emailVerified: false
        };

        await set(ref(db, `users/${user.uid}`), adminProfile);
        showAlert(`PROTOCOL_AUTHORIZED: Identity provisioned as ${customId}. Please verify your email.`, "success");
      }
    } catch (error) {
      showAlert(error.message || "Administrative Access Denied", "error");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return <div className="admin-auth-loading">Verifying Security Credentials...</div>;

  return (
    <div className="admin-auth-container">
      <div className="admin-auth-overlay"></div>
      
      <div className="admin-auth-box">
        {/* Tactical HUD Corner Brackets */}
        <div className="hud-corner-tl"></div>
        <div className="hud-corner-br"></div>

        <header className="admin-auth-header">
          <div className="admin-brand-icon">
            <ShieldCheck size={36} strokeWidth={2.5} />
          </div>
          <h1>System Command</h1>
          <p>Admin Authorization Node</p>
          <div className="header-decoration">
            <span className="metadata">SRC: 192.168.1.104</span>
            <span className="metadata">AUTH_LVL: 7</span>
          </div>
        </header>

        <div className="admin-auth-tabs">
          <button 
            className={isLogin ? "active" : ""} 
            onClick={() => setIsLogin(true)}
          >
            TERMINAL_IN
          </button>
          <button 
            className={!isLogin ? "active" : ""} 
            onClick={() => setIsLogin(false)}
          >
            PROVISION_NODE
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-auth-form">
          {!isLogin && (
            <div className="admin-input-group">
              <label><User size={14} /> IDENTIFIER</label>
              <input
                type="text"
                name="name"
                placeholder="Full Legal Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <div className="admin-input-group">
            <label><Mail size={14} /> SECURITY_MAIL</label>
            <input
              type="email"
              name="email"
              placeholder="admin.id@medico.in"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="admin-input-group">
            <label><Lock size={14} /> ENCRYPTION_KEY</label>
            <input
              type="password"
              name="password"
              placeholder="••••••••••••"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <button className="admin-submit-btn" type="submit" disabled={loading}>
            {loading ? (
              <>AUTHORIZING...</>
            ) : (
              <>
                {isLogin ? "EXECUTE_ACCESS" : "INITIALIZE_NODE"}
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        <footer className="admin-auth-footer">
          <div className="security-notice">
            <Activity size={12} className="pulse" />
            <span>ENCRYPTED_TUNNEL: ACTIVE</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AdminAuthPage;
